import classNames from 'classnames'
import { useMemo } from 'react'

import { Button, IButtonProps } from 'core-components/library/button'
import { Loader } from 'core-components/library/loader'
import { ITabButtonsProps, ITabProps } from 'core-components/library/tabButtons'
import { ERROR_MESSAGE } from 'core-components/library/tabButtons/constants/validation'
import { ITabValidators } from 'core-components/library/tabButtons/models/ITab'
import { Tooltip } from 'core-components/library/tooltip'

import styles from './base/BaseTabButtons.module.scss'

interface ITab {
  items: ITabButtonsProps['items']
  activeTab: string
  onChangeTab: (newKey: string) => void
  tabLoaderVisible?: boolean
  tabsValidators?: ITabValidators
  titleRender?: ITabButtonsProps['titleRender']
  classes?: ITabButtonsProps['classes']
  contentAlwaysExisting?: ITabButtonsProps['contentAlwaysExisting']
  headerContent?: ITabButtonsProps['headerContent']
}

export const Tabs = ({
  items,
  tabsValidators = {},
  activeTab,
  classes,
  contentAlwaysExisting,
  onChangeTab,
  titleRender,
  tabLoaderVisible,
  headerContent,
}: ITab) => {
  const visibleTabs = useMemo(() => {
    return items.filter((tab) => tab.viewCond !== false)
  }, [items])

  const getButtonSettings = (item: ITabProps, isInvalid: boolean): IButtonProps => {
    const isActiveTab = item.id === activeTab
    const config: IButtonProps = {
      className: classNames(
        styles.button,
        classes?.button || '',
        isActiveTab ? classes?.buttonActive || styles['button--selected'] : '',
        !isActiveTab && isInvalid ? styles['button--invalid'] : ''
      ),
      disabled: item.disabled,
      __testId: item.id,
      variant: 'text',
      color: 'grey',
      role: 'tab',
      id: `baseTabButtons-tab-${item.id}`,
      ariaSelected: isActiveTab,
      ariaControls: `baseTabButtons-panel-${item.id}`,
    }
    config.onClick = () => onChangeTab(item.id)

    return config
  }

  const callTitleRender = (item: ITabProps) => {
    if (item.title) {
      return item.title()
    }
    if (titleRender) {
      return titleRender(item.label)
    }
    return item.label
  }

  const getTabButton = (
    item: ITabProps,
    buttonConfig: IButtonProps,
    isActive: boolean,
    isInvalid: boolean
  ) => {
    return (
      <Button {...buttonConfig} key={item.id}>
        <span>
          {callTitleRender(item)}{' '}
          {!isActive && isInvalid ? <div className={'kbase kbase-warning-alt'}></div> : null}
          {tabLoaderVisible && isActive ? (
            <Loader className={styles.titleLoader} dimension={{ width: 10, height: 10 }} />
          ) : null}
        </span>
      </Button>
    )
  }

  return (
    <>
      {visibleTabs.map((item) => {
        const isInvalid = tabsValidators.hasOwnProperty(item.id) && !tabsValidators[item.id]
        const isActive = item.id === activeTab
        const buttonConfig = getButtonSettings(item, isInvalid)
        const canShowTooltip = !!contentAlwaysExisting

        if (canShowTooltip) {
          return (
            <Tooltip
              key={item.id}
              disabled={!isInvalid || isActive}
              tipContent={() => <span>{ERROR_MESSAGE}</span>}
            >
              {getTabButton(item, buttonConfig, isActive, isInvalid)}
            </Tooltip>
          )
        } else {
          return getTabButton(item, buttonConfig, isActive, isInvalid)
        }
      })}
      {headerContent ? <div className={styles.headerContent}>{headerContent()}</div> : null}
    </>
  )
}
